/**
 * Behavior-preset blob carried on `KioskTemplate.config` AND
 * `Kiosk.config`. One canonical shape — the template's blob copies
 * into the kiosk's blob on Load-Preset, no key remapping.
 *
 * Mirrors `App\Lib\Defaults\KioskConfigDefaults` (PHP) verbatim —
 * keep the two in lockstep when extending the preset surface.
 * Adding a new knob = one field on the matching nested section +
 * one form input on the FE editor + one validation rule on the BE
 * + one default in `KioskConfigDefaults::all()`. ZERO DDL.
 */

export type LateQualificationUnit = 'minutes' | 'hours'
export type DurationUnit = 'seconds' | 'minutes' | 'hours'
export type IdentificationMethod = 'device' | 'user' | 'both'
export type ReturnFlow = 'device_first' | 'user_first'
/**
 * High-level kiosk workflow mode.
 *   - `direct_assignment`: identity-gated; a specific user is
 *     granted a specific bay
 *   - `anonymous`: hotel-room-style; a 6-digit passcode is issued
 *     at check-in and used to retrieve the device
 *   - `hybrid`: both workflows active simultaneously, partitioned
 *     per-bay on the kiosk side via `bay_list.bay_mode`
 */
export type OperationMode = 'direct_assignment' | 'anonymous' | 'hybrid'

/**
 * AC (anonymous-checkout) check-in identification gate.
 *   - `pin_only`: no identity step; passcode is the session anchor
 *   - `user_auth`: user ID + stored PIN required before passcode
 */
export type AcCheckInIdentificationMethod = 'pin_only' | 'user_auth'

/**
 * Where the AC session's locker passcode comes from.
 *   - `user_entered`: operator types it (adds passcode-entry step)
 *   - `system_generated`: kiosk mints + displays 6-digit code
 *   - `id_as_pin`: the person's ID is the passcode, no user lookup —
 *     offered under `user_auth` only (the server refuses it otherwise)
 */
export type AcPinSource = 'user_entered' | 'system_generated' | 'id_as_pin'

/**
 * Per-branding-slot display mode synced to the kiosk
 * (`operation.branding.<slot>`):
 *   - `off`: kiosk renders nothing for that slot
 *   - `default`: kiosk renders its compiled-in default (stock)
 *   - `custom`: kiosk renders the server-supplied asset if present,
 *     else the default
 *
 * Asset file transfer is deferred — only this mode toggle syncs today.
 */
export type BrandingSlotMode = 'off' | 'default' | 'custom'

/** Brandable slot tokens — match the kiosk `BrandingSlot.name.lowercase()`. */
export type BrandingSlot =
  | 'splash'
  | 'screensaver'
  | 'ac_checkin'
  | 'ac_checkout'
  | 'dc_checkin'
  | 'dc_checkout'
  | 'hybrid_public'
  | 'hybrid_assigned'
  | 'device_sample'
  | 'scan_sample'

export const BRANDING_SLOTS: readonly BrandingSlot[] = [
  'splash',
  'screensaver',
  'ac_checkin',
  'ac_checkout',
  'dc_checkin',
  'dc_checkout',
  'hybrid_public',
  'hybrid_assigned',
  'device_sample',
  'scan_sample',
]

function defaultBranding(): Record<BrandingSlot, BrandingSlotMode> {
  return Object.fromEntries(
    BRANDING_SLOTS.map((s) => [s, 'default' as BrandingSlotMode]),
  ) as Record<BrandingSlot, BrandingSlotMode>
}

export interface LateQualificationThreshold {
  value: number
  unit: LateQualificationUnit
}

export interface Duration {
  value: number
  unit: DurationUnit
}

export interface KioskConfigCheckIn {
  late_qualification: {
    enabled: boolean
    threshold: LateQualificationThreshold
  }
  identification_method: IdentificationMethod
  /** Surfaced when `identification_method` ∈ {user, both}. */
  request_pin: boolean
  /**
   * Device condition report at check-in. Off = no condition step UI:
   * a device identified at return is recorded as good.
   */
  condition_report_enabled: boolean
  /**
   * User ID / device ID regex extract (blank = off). A scan used as a user ID
   * (device ID) is matched, and reported, as the pattern's first capture
   * group or its whole match. Not applied while `scanner.regex_extract` is
   * set. Surfaced when `identification_method` ∈ {user, both} /
   * {device, both}.
   */
  user_id_regex_extract: string
  device_id_regex_extract: string
}

export interface KioskConfigCheckout {
  request_pin: boolean
}

export interface KioskConfigOperation {
  /**
   * High-level kiosk workflow mode. Replaces the v2
   * `self_registration.anonymous_checkout_enabled` boolean —
   * anonymous is now a named operation mode, not a sub-flag.
   */
  mode: OperationMode
  door_open_timeout: Duration
  /**
   * Per-step idle ceiling on any operator-facing action flow
   * (check-in, check-out). When the operator has been idle for
   * this long during a step, the flow self-cancels and returns
   * the kiosk to the home screen. Paired across kiosk + server
   * 2026-05-22 — previously a hardcoded
   * `ActionFlow.DEFAULT_TIMEOUT = PerStepReset(30s)` on the kiosk.
   */
  action_flow_idle_timeout: Duration
  uvc_kick_in_delay: Duration
  /**
   * How long the UVC sanitization cycle runs after
   * `uvc_kick_in_delay` elapses. Bay sits in CLEANING for this.
   */
  cleaning_duration: Duration
  /**
   * Safety ceiling for CHARGING state after cleaning completes
   * (DC mode). On elapse the bay transitions to AVAILABLE.
   */
  max_charging_time: Duration
  screensaver_enabled: boolean
  /** Idle window before the screensaver engages (ignored when disabled). */
  screensaver_idle_timeout: Duration
  /** Per-slot branding mode, keyed by slot token. */
  branding: Record<BrandingSlot, BrandingSlotMode>
  return_flow: ReturnFlow
  /**
   * Home-screen passive bay-restock handler. Only meaningful
   * when `mode != 'anonymous'` — FE hides the toggle in
   * anonymous mode.
   */
  passive_restock_enabled: boolean
  /**
   * Home-screen passive check-in (device return) handler. Only
   * meaningful when `mode != 'anonymous'` — FE hides the toggle
   * in anonymous mode.
   */
  passive_check_in_enabled: boolean
  /**
   * Home-screen passive checkout (by member ID) handler. Only
   * meaningful when `mode != 'anonymous'` — FE hides the toggle
   * in anonymous mode.
   */
  passive_checkout_enabled: boolean
}

/**
 * AC (anonymous-checkout) check-in workflow settings. Only
 * meaningful when `operation.mode ∈ {'anonymous', 'hybrid'}` —
 * FE hides this section entirely when mode is direct_assignment.
 */
export interface KioskConfigAcCheckIn {
  identification_method: AcCheckInIdentificationMethod
  pin_source: AcPinSource
}

export interface KioskConfigSelfRegistration {
  enabled: boolean
}

/**
 * Scanner. `regex_extract` (blank = off): applied by the kiosk to every
 * scan from every scanner before anything reads it — a match keeps the
 * first capture group, or the whole match; a scan it doesn't match passes
 * unchanged. While set, the user ID / device ID extracts are not applied.
 */
export interface KioskConfigScanner {
  regex_extract: string
}

/**
 * Hardware capabilities (config-of-record). Whether the kiosk
 * honors each scan-credential reader; synced 1:1 and gated on the
 * kiosk at credential resolution (rfid off → scanned values no
 * longer matched against `users.rfid_tag`; nfc off → not matched
 * against `users.nfc_tag`).
 *
 * ONLY the two real, gatable readers live here. `usb_scanner` /
 * `ota` are NOT config-of-record — they have no kiosk consumer, so
 * the live `set_kiosk_capability` command honest-fails them.
 */
export interface KioskConfigCapabilities {
  rfid_scanner: boolean
  nfc_scanner: boolean
}

/**
 * Smart charging. `enabled` detects when a device is full and cuts its USB
 * power; off = no detection, USB stays on while a device rests in the bay
 * (a device model's `charging_mode = 'bypass'` does the same for that model
 * only). `maintain_charge` is part of smart charging — ignored when it is
 * off: after a device is full, smart charge runs again once
 * `maintain_charge_wait` elapses, and repeats, while the bay stays in service.
 */
export interface KioskConfigSmartCharging {
  enabled: boolean
  maintain_charge: boolean
  maintain_charge_wait: Duration
}

export interface KioskConfig {
  check_in: KioskConfigCheckIn
  checkout: KioskConfigCheckout
  operation: KioskConfigOperation
  smart_charging: KioskConfigSmartCharging
  ac_check_in: KioskConfigAcCheckIn
  self_registration: KioskConfigSelfRegistration
  scanner: KioskConfigScanner
  capabilities: KioskConfigCapabilities
}

/**
 * Canonical default config — used as the merge base when a
 * template or kiosk row carries `config = null` (fresh row, or
 * legacy row that pre-dates the column). FE editors form-bind
 * over this so the operator never sees an empty-state form.
 *
 * MUST stay in lockstep with `App\Lib\Defaults\KioskConfigDefaults::all()`
 * on the BE — any divergence means the form initializes with one
 * shape and the wire validation accepts another, producing
 * silent-overwrite-on-save bugs.
 */
export const KIOSK_CONFIG_DEFAULTS: KioskConfig = {
  check_in: {
    late_qualification: {
      enabled: false,
      threshold: { value: 15, unit: 'minutes' },
    },
    identification_method: 'device',
    request_pin: false,
    condition_report_enabled: true,
    user_id_regex_extract: '',
    device_id_regex_extract: '',
  },
  checkout: {
    request_pin: false,
  },
  operation: {
    mode: 'anonymous',
    door_open_timeout: { value: 60, unit: 'seconds' },
    action_flow_idle_timeout: { value: 30, unit: 'seconds' },
    uvc_kick_in_delay: { value: 30, unit: 'seconds' },
    cleaning_duration: { value: 30, unit: 'seconds' },
    max_charging_time: { value: 8, unit: 'hours' },
    screensaver_enabled: true,
    screensaver_idle_timeout: { value: 5, unit: 'minutes' },
    branding: defaultBranding(),
    return_flow: 'device_first',
    passive_restock_enabled: true,
    passive_check_in_enabled: true,
    passive_checkout_enabled: true,
  },
  smart_charging: {
    enabled: true,
    maintain_charge: false,
    maintain_charge_wait: { value: 2, unit: 'hours' },
  },
  ac_check_in: {
    identification_method: 'pin_only',
    pin_source: 'user_entered',
  },
  self_registration: {
    enabled: false,
  },
  scanner: {
    regex_extract: '',
  },
  capabilities: {
    rfid_scanner: true,
    nfc_scanner: true,
  },
}

/**
 * Deep-clone the canonical defaults — for use as a form-init
 * value where the operator's edits MUST NOT mutate the shared
 * constant. Cheapest deep clone on a known-pure-data shape is
 * `JSON.parse(JSON.stringify(...))`; we don't carry Dates / Maps
 * / class instances in this blob.
 */
export function cloneKioskConfigDefaults(): KioskConfig {
  return JSON.parse(JSON.stringify(KIOSK_CONFIG_DEFAULTS)) as KioskConfig
}

/**
 * Hydrate a form-shaped config from a row's possibly-partial
 * `config` blob, falling back to defaults for any missing keys.
 * Deep merge through every nested level — operator can save a
 * row that only overrides one nested key (e.g. door-open
 * timeout) and the rest stay at the canonical defaults.
 */
export function hydrateKioskConfig(input: Partial<KioskConfig> | null | undefined): KioskConfig {
  const base = cloneKioskConfigDefaults()
  if (!input) return base
  return {
    check_in: {
      late_qualification: {
        enabled: input.check_in?.late_qualification?.enabled
          ?? base.check_in.late_qualification.enabled,
        threshold: {
          value: input.check_in?.late_qualification?.threshold?.value
            ?? base.check_in.late_qualification.threshold.value,
          unit: input.check_in?.late_qualification?.threshold?.unit
            ?? base.check_in.late_qualification.threshold.unit,
        },
      },
      identification_method: input.check_in?.identification_method
        ?? base.check_in.identification_method,
      request_pin: input.check_in?.request_pin ?? base.check_in.request_pin,
      condition_report_enabled: input.check_in?.condition_report_enabled
        ?? base.check_in.condition_report_enabled,
      user_id_regex_extract: input.check_in?.user_id_regex_extract
        ?? base.check_in.user_id_regex_extract,
      device_id_regex_extract: input.check_in?.device_id_regex_extract
        ?? base.check_in.device_id_regex_extract,
    },
    checkout: {
      request_pin: input.checkout?.request_pin ?? base.checkout.request_pin,
    },
    operation: {
      mode: input.operation?.mode ?? base.operation.mode,
      door_open_timeout: {
        value: input.operation?.door_open_timeout?.value
          ?? base.operation.door_open_timeout.value,
        unit: input.operation?.door_open_timeout?.unit
          ?? base.operation.door_open_timeout.unit,
      },
      action_flow_idle_timeout: {
        value: input.operation?.action_flow_idle_timeout?.value
          ?? base.operation.action_flow_idle_timeout.value,
        unit: input.operation?.action_flow_idle_timeout?.unit
          ?? base.operation.action_flow_idle_timeout.unit,
      },
      uvc_kick_in_delay: {
        value: input.operation?.uvc_kick_in_delay?.value
          ?? base.operation.uvc_kick_in_delay.value,
        unit: input.operation?.uvc_kick_in_delay?.unit
          ?? base.operation.uvc_kick_in_delay.unit,
      },
      cleaning_duration: {
        value: input.operation?.cleaning_duration?.value
          ?? base.operation.cleaning_duration.value,
        unit: input.operation?.cleaning_duration?.unit
          ?? base.operation.cleaning_duration.unit,
      },
      max_charging_time: {
        value: input.operation?.max_charging_time?.value
          ?? base.operation.max_charging_time.value,
        unit: input.operation?.max_charging_time?.unit
          ?? base.operation.max_charging_time.unit,
      },
      screensaver_enabled: input.operation?.screensaver_enabled
        ?? base.operation.screensaver_enabled,
      screensaver_idle_timeout: {
        value: input.operation?.screensaver_idle_timeout?.value
          ?? base.operation.screensaver_idle_timeout.value,
        unit: input.operation?.screensaver_idle_timeout?.unit
          ?? base.operation.screensaver_idle_timeout.unit,
      },
      branding: Object.fromEntries(
        BRANDING_SLOTS.map((s) => [s, input.operation?.branding?.[s] ?? base.operation.branding[s]]),
      ) as Record<BrandingSlot, BrandingSlotMode>,
      return_flow: input.operation?.return_flow ?? base.operation.return_flow,
      passive_restock_enabled: input.operation?.passive_restock_enabled
        ?? base.operation.passive_restock_enabled,
      passive_check_in_enabled: input.operation?.passive_check_in_enabled
        ?? base.operation.passive_check_in_enabled,
      passive_checkout_enabled: input.operation?.passive_checkout_enabled
        ?? base.operation.passive_checkout_enabled,
    },
    smart_charging: {
      enabled: input.smart_charging?.enabled ?? base.smart_charging.enabled,
      maintain_charge: input.smart_charging?.maintain_charge
        ?? base.smart_charging.maintain_charge,
      maintain_charge_wait: {
        value: input.smart_charging?.maintain_charge_wait?.value
          ?? base.smart_charging.maintain_charge_wait.value,
        unit: input.smart_charging?.maintain_charge_wait?.unit
          ?? base.smart_charging.maintain_charge_wait.unit,
      },
    },
    ac_check_in: {
      identification_method: input.ac_check_in?.identification_method
        ?? base.ac_check_in.identification_method,
      pin_source: input.ac_check_in?.pin_source ?? base.ac_check_in.pin_source,
    },
    self_registration: {
      enabled: input.self_registration?.enabled ?? base.self_registration.enabled,
    },
    scanner: {
      regex_extract: input.scanner?.regex_extract ?? base.scanner.regex_extract,
    },
    capabilities: {
      rfid_scanner: input.capabilities?.rfid_scanner ?? base.capabilities.rfid_scanner,
      nfc_scanner: input.capabilities?.nfc_scanner ?? base.capabilities.nfc_scanner,
    },
  }
}
